import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

import { projectsApi } from '../services/api';
import { Button, Card, Badge, Input, PageHeader, Spinner } from '../components/ui';

/*
|--------------------------------------------------------------------------
| Projects Page
|--------------------------------------------------------------------------
| Lists every project. A project is a named container that groups the
| Domo apps/pages (reports) selected for an assessment, plus the users
| assigned to work on it and a discussion thread of comments. Locking a
| project freezes it — no further edits to its details, resources, or
| assigned users are allowed until it's unlocked.
|--------------------------------------------------------------------------
*/

export default function ProjectsPage() {

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  const { data, isLoading, isError, error } = useQuery(
    'projects',
    () => projectsApi.list(),
    { refetchOnWindowFocus: false }
  );

  const projects = data?.data?.data || data?.data || [];

  const createMutation = useMutation(
    (body) => projectsApi.create(body),
    {
      onSuccess: (resp) => {
        toast.success('Project created');
        queryClient.invalidateQueries('projects');
        setShowCreateModal(false);
        const newId = resp?.data?.data?.id || resp?.data?.id;
        if (newId) {
          navigate(`/projects/${newId}`);
        }
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || err.message || 'Failed to create project');
      }
    }
  );

  const mergeMutation = useMutation(
    (body) => projectsApi.merge(body),
    {
      onSuccess: (resp) => {
        toast.success('Projects merged');
        queryClient.invalidateQueries('projects');
        setShowMergeModal(false);
        const newId = resp?.data?.data?.id || resp?.data?.id;
        if (newId) {
          navigate(`/projects/${newId}`);
        }
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || err.message || 'Failed to merge projects');
      }
    }
  );

  return (
    <div style={{ padding: '24px' }}>

      <PageHeader
        title="Projects"
        subtitle="Group apps and pages into a project, assign a team, and track progress before running an assessment."
        actions={
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="secondary" onClick={() => setShowMergeModal(true)} disabled={projects.length < 2}>
              Merge Projects
            </Button>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              + New Project
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <Spinner />
        </div>
      ) : isError ? (
        <Card>
          <div style={{ color: 'var(--danger)', fontSize: 13 }}>
            Failed to load projects: {error?.response?.data?.error || error?.message}
          </div>
        </Card>
      ) : !projects.length ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>
              No projects yet
            </div>
            <div style={{ fontSize: 13, marginBottom: 16 }}>
              Create a project to start grouping apps/pages for assessment.
            </div>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              + New Project
            </Button>
          </div>
        </Card>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 14
          }}
        >
          {projects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => navigate(`/projects/${project.id}`)}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onCreate={body => createMutation.mutate(body)}
          saving={createMutation.isLoading}
        />
      )}

      {showMergeModal && (
        <MergeProjectsModal
          projects={projects}
          onClose={() => setShowMergeModal(false)}
          onMerge={body => mergeMutation.mutate(body)}
          saving={mergeMutation.isLoading}
        />
      )}

    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Project Card
|--------------------------------------------------------------------------
*/

function ProjectCard({ project, onClick }) {

  const resourceCount = project.resources?.length ?? project.resourceCount ?? 0;
  const userCount = project.users?.length ?? project.userCount ?? 0;
  const commentCount = project.comments?.length ?? project.commentCount ?? 0;

  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--bg-card)',
        border: '0.5px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '16px 18px',
        cursor: 'pointer',
        boxShadow: 'var(--shadow-sm)',
        transition: 'box-shadow .15s, transform .15s'
      }}
      onMouseOver={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseOut={e => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
          {project.name}
        </div>
        {project.locked ? (
          <Badge variant="warning">🔒 Locked</Badge>
        ) : (
          <Badge variant="success">Open</Badge>
        )}
      </div>

      {project.description && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-secondary)',
            marginBottom: 12,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {project.description}
        </div>
      )}

      <div style={{ display: 'flex', gap: 14, fontSize: 11.5, color: 'var(--text-tertiary)' }}>
        <span>{resourceCount} app{resourceCount === 1 ? '' : 's'}/report{resourceCount === 1 ? '' : 's'}</span>
        <span>{userCount} user{userCount === 1 ? '' : 's'}</span>
        <span>{commentCount} comment{commentCount === 1 ? '' : 's'}</span>
      </div>
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Create Project Modal
|--------------------------------------------------------------------------
*/

function CreateProjectModal({ onClose, onCreate, saving }) {

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const canCreate = name.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create project"
      onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24
      }}
    >
      <div
        style={{
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-lg)',
          maxWidth: 460,
          width: '100%',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '0.5px solid var(--border)',
            fontSize: 15,
            fontWeight: 600
          }}
        >
          New Project
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            label="Project name"
            placeholder="e.g. Q3 Sales Dashboards Migration"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              placeholder="What is this project for?"
              style={{
                padding: '8px 10px',
                border: '0.5px solid var(--border-med)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                fontSize: 13,
                outline: 'none',
                fontFamily: 'var(--font-sans)',
                resize: 'vertical'
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 20px',
            borderTop: '0.5px solid var(--border)'
          }}
        >
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canCreate}
            loading={saving}
            onClick={() => onCreate({ name: name.trim(), description: description.trim() })}
          >
            Create Project
          </Button>
        </div>
      </div>
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Merge Projects Modal
|--------------------------------------------------------------------------
| Combines two or more existing projects into a single new project. The
| payload sent to the backend is intentionally minimal — just the new
| project's name and the ids of the projects being merged. The backend
| owns the logic for consolidating resources/users/comments/history.
|--------------------------------------------------------------------------
*/

function MergeProjectsModal({ projects, onClose, onMerge, saving }) {

  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const toggleId = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const allSelected = selectedIds.length === projects.length;
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : projects.map(p => p.id));
  };

  const canMerge = name.trim().length > 0 && selectedIds.length >= 2;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Merge projects"
      onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24
      }}
    >
      <div
        style={{
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-lg)',
          maxWidth: 460,
          width: '100%',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '0.5px solid var(--border)',
            fontSize: 15,
            fontWeight: 600
          }}
        >
          Merge Projects
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
          <Input
            label="New project name"
            placeholder="e.g. Combined Sales Migration"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>
                Select projects to merge ({selectedIds.length} selected)
              </label>
              <button
                type="button"
                onClick={toggleSelectAll}
                style={{
                  border: 'none',
                  background: 'none',
                  color: 'var(--accent)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </button>
            </div>

            <div
              style={{
                border: '0.5px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                maxHeight: 260,
                overflowY: 'auto'
              }}
            >
              {projects.map(project => {
                const checked = selectedIds.includes(project.id);
                return (
                  <label
                    key={project.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '9px 12px',
                      borderBottom: '0.5px solid var(--border)',
                      cursor: 'pointer',
                      background: checked ? 'var(--accent-light)' : 'transparent'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleId(project.id)}
                    />
                    <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                      {project.name}
                    </span>
                    {project.locked && (
                      <span style={{ marginLeft: 'auto' }}>
                        <Badge variant="warning">🔒</Badge>
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 20px',
            borderTop: '0.5px solid var(--border)'
          }}
        >
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canMerge}
            loading={saving}
            onClick={() => onMerge({ name: name.trim(), projectIds: selectedIds })}
          >
            Merge Projects
          </Button>
        </div>
      </div>
    </div>
  );
}
